var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { flatMap } from 'lodash';
import { DiscountType } from '../plugins/commonTypes/CommonTypes';
import { getConfigPriceModifierG6, computeNodeTotalG1 } from './OrderPricingPipelineG6';
import { G6LineType, G6ReceiptLineType } from './OrderLineTypesG6';
const formatAmount = (amount, quantity) => {
    if (quantity) {
        return Math.round(parseFloat(amount) * quantity * 100) / 100;
    }
    else {
        return Math.round(parseFloat(amount) * 100) / 100;
    }
};
const getProductConfigurations = (node) => {
    return (node.subLines || [])
        .filter((line) => line.type === G6LineType.Config)
        .map((configLine) => {
        return {
            name: configLine.name,
            selectedOption: configLine.selectedOption,
        };
    });
};
const ingredientsDescription = (node, quantity, depth) => {
    if (Array.isArray(node.productIngredients) && node.productIngredients.length > 1) {
        return node.productIngredients.map((pi) => {
            return {
                text: pi.ingredient ? pi.ingredient.name : 'Unknown ingredient - please contact administrator for help',
                amount: `${formatAmount(pi.amount, quantity)} ${pi.ingredient ? pi.ingredient.unit : 'Unknown ingredient - please contact administrator for help'}`,
                depth: depth,
            };
        });
    }
    else {
        return [];
    }
};
const singleIngredientAmount = (node, quantity) => {
    if (Array.isArray(node.productIngredients) && node.productIngredients.length === 1) {
        const pi = node.productIngredients[0];
        if (pi.ingredient) {
            return `${formatAmount(pi.amount, quantity)} ${pi.ingredient.unit}`;
        }
        else {
            return `${formatAmount(pi.amount, quantity)}, Unknown ingredient - please contact administrator for help!`;
        }
    }
    else {
        return '';
    }
};
export const getDescriptionG6 = (node, { quantity, absoluteRootProductId, depth = 0, changeFont = false }) => {
    const quantityText = typeof node.quantity === 'number' ? `${node.quantity} x ` : '';
    const productQuantity = node.options && node.options.countPerAmount && !node.includedInMainPrice && typeof node.quantity === 'number'
        ? node.quantity
        : quantity;
    const pricePerSubline = node.price && node.includedInMainPrice !== true ? node.price : null;
    const totalSublinePrice = pricePerSubline ? formatAmount(node.price, productQuantity) : null;
    const unitType = pricePerSubline ? node.unitType : null;
    const descriptionLine = {
        text: quantityText + (node.name || ''),
        amount: singleIngredientAmount(node, quantity),
        depth: depth,
        changeFont: changeFont,
        pricePerSubline,
        totalSublinePrice,
        price: pricePerSubline,
        unitType,
        quantity: typeof node.quantity === 'number' ? node.quantity : null,
        productId: node.productId ? node.productId : null,
        ean: node.ean ? node.ean : null,
        plu: node.plu ? node.plu : null,
        absoluteRootProductId: absoluteRootProductId ? absoluteRootProductId : null,
        rootProductId: node.rootProductId ? node.rootProductId : null,
    };
    const ingredientsDescriptions = ingredientsDescription(node, quantity, depth + 1);
    const emptyValues = [null, undefined, ''];
    if (emptyValues.includes(node.name)) {
        const subDescriptions = node.subLines && node.subLines.length
            ? flatMap(node.subLines, (line) => getDescriptionG6(line, {
                quantity: quantity,
                depth,
                absoluteRootProductId,
                changeFont: true,
            }))
            : [];
        return [...ingredientsDescriptions, ...subDescriptions];
    }
    else {
        const subDescriptions = node.subLines && node.subLines.length
            ? flatMap(node.subLines, (line) => getDescriptionG6(line, {
                quantity,
                depth: depth + 1,
                absoluteRootProductId,
            }))
            : [];
        return [descriptionLine, ...ingredientsDescriptions, ...subDescriptions];
    }
};
export const getReceiptLineG6 = (node, { groupStart = true, orderIndex, absoluteRootProductId }) => {
    if (node.type === G6LineType.Aggregation) {
        return {
            generation: 6,
            orderIndex,
            type: G6ReceiptLineType.Section,
            groupStart: groupStart,
            name: node.name,
            options: node.options,
            productId: node.productId,
            discount: node.discount,
            addedByGame: !!node.addedByGame,
            absoluteRootProductId,
            productConfigurations: getProductConfigurations(node),
            quantity: undefined,
            amount: undefined,
            description: undefined,
            price: undefined,
            ean: undefined,
            plu: undefined,
            totalPrice: undefined,
            unitType: undefined,
            rootProductId: undefined,
            configPriceModifier: undefined,
        };
    }
    else if (node.type === G6LineType.Delivery) {
        return {
            generation: 6,
            productConfigurations: [],
            orderIndex,
            type: G6ReceiptLineType.Delivery,
            quantity: node.quantity,
            groupStart: groupStart,
            name: node.name,
            price: node.price,
            ean: node.ean,
            plu: node.plu,
            unitType: node.unitType,
            totalPrice: computeNodeTotalG1(node),
            addedByGame: !!node.addedByGame,
        };
    }
    else if (node.type === G6LineType.ReturnableItem) {
        return {
            generation: 6,
            productConfigurations: [],
            orderIndex,
            type: G6ReceiptLineType.ReturnableItem,
            quantity: node.quantity,
            groupStart: groupStart,
            options: node.options,
            name: node.name,
            price: node.price,
            ean: node.ean,
            discount: node.discount,
            addedByGame: !!node.addedByGame,
            plu: node.plu,
            unitType: node.unitType,
            totalPrice: computeNodeTotalG1(node),
        };
    }
    else {
        const quantity = node.quantity;
        const subLinesDescription = node.subLines
            ? flatMap(node.subLines, (line) => getDescriptionG6(line, {
                quantity,
                absoluteRootProductId,
            }))
            : [];
        const description = [...ingredientsDescription(node, node.quantity, 0), ...subLinesDescription];
        return {
            generation: 6,
            orderIndex,
            type: G6ReceiptLineType.Product,
            quantity: node.quantity,
            groupStart: groupStart,
            name: node.name,
            amount: singleIngredientAmount(node, node.quantity),
            productId: node.productId,
            rootProductId: node.rootProductId,
            absoluteRootProductId,
            price: node.price,
            ean: node.ean,
            plu: node.plu,
            discount: node.discount,
            addedByGame: !!node.addedByGame,
            options: node.options,
            unitType: node.unitType,
            configPriceModifier: getConfigPriceModifierG6(node),
            totalPrice: computeNodeTotalG1(node),
            description: description.length > 0 ? description : '',
            productConfigurations: getProductConfigurations(node),
        };
    }
};
const projectReceiptLines = (topLevelOrderLines) => {
    const receiptLines = [];
    const applyInheritedNoMainProductDiscount = (receiptLine, inheritedNoMainProductDiscount) => {
        if (!inheritedNoMainProductDiscount || receiptLine.discount) {
            return receiptLine;
        }
        if (receiptLine.type === G6ReceiptLineType.Product &&
            inheritedNoMainProductDiscount.discountType === DiscountType.percentDiscount &&
            typeof receiptLine.price === 'number' &&
            !receiptLine.discount) {
            receiptLine.discount = inheritedNoMainProductDiscount;
            const remainingPercent = 100 - inheritedNoMainProductDiscount.discountValue;
            if (remainingPercent > 0) {
                const oldPrice = (receiptLine.price * 100) / remainingPercent;
                receiptLine.oldPrice = oldPrice;
                receiptLine.oldTotalPrice = receiptLine.quantity * oldPrice;
            }
        }
        return receiptLine;
    };
    const recurse = function (line, { groupStart, orderIndex, absoluteRootProductId, isParentAggregation, inheritedNoMainProductDiscount }) {
        if (line.type === G6LineType.Config || line.type === G6LineType.Discount) {
            return;
        }
        let myGroupStart = groupStart;
        const currentInheritedNoMainProductDiscount = line.type === G6LineType.Aggregation && line.discount ? line.discount : inheritedNoMainProductDiscount;
        if ((line.type === G6LineType.Aggregation && (line.name === line.subLines[0].name || isParentAggregation !== true)) ||
            (line.type === G6LineType.Product && line.includedInMainPrice === true)) {
        }
        else {
            const receiptLine = applyInheritedNoMainProductDiscount(getReceiptLineG6(line, {
                groupStart,
                orderIndex,
                absoluteRootProductId,
            }), currentInheritedNoMainProductDiscount);
            receiptLines.push(receiptLine);
            myGroupStart = false;
        }
        if (line.type !== G6LineType.Aggregation ||
            (line.type === G6LineType.Aggregation && isParentAggregation === true)) {
            (line.subLines || []).forEach((subline) => recurse(subline, {
                groupStart: myGroupStart,
                orderIndex,
                absoluteRootProductId,
                isParentAggregation: line.type === G6LineType.Aggregation,
                inheritedNoMainProductDiscount: currentInheritedNoMainProductDiscount,
            }));
        }
    };
    if (topLevelOrderLines) {
        topLevelOrderLines.forEach((line, index) => {
            recurse(line, {
                groupStart: true,
                orderIndex: index,
                absoluteRootProductId: line.rootProductId,
                isParentAggregation: true,
                inheritedNoMainProductDiscount: undefined,
            });
        });
    }
    return receiptLines;
};
const applyPresentationDiscounts = (receiptLines, forViking = false) => {
    const singleLineProductQuantity = {};
    const formattedReceiptLines = [];
    const isSingleSelectionProduct = (receiptLine) => {
        return receiptLine.options && receiptLine.options.canHaveMultipleProductSelections;
    };
    const calculateSimpleDiscountFixedAmount = (line) => {
        if (!isNaN(line.discount.discountValue) && typeof line.discount.discountValue === 'number') {
            const discountedPrice = line.price * 100 - line.discount.discountValue * 100;
            return discountedPrice >= 0 ? discountedPrice / 100 : line.price;
        }
        return line.price;
    };
    const isNumber = (number) => {
        return typeof number === 'number' && !isNaN(number);
    };
    const canApplyDiscount = (receiptLine) => {
        if (!receiptLine.discount) {
            return false;
        }
        const discountApplicableGames = (() => {
            if (!receiptLine.discount.options) {
                return false;
            }
            if (receiptLine.discount.options.giftDiscount === undefined ||
                receiptLine.discount.options.giftDiscount === null) {
                return true;
            }
            else if (receiptLine.discount.options.giftDiscount === true) {
                return !!receiptLine.addedByGame;
            }
            return false;
        })();
        return discountApplicableGames;
    };
    const noMainProductDiscount = {};
    for (const i in receiptLines) {
        const receiptLine = receiptLines[i];
        const { price } = receiptLine, trimmedLine = __rest(receiptLine, ["price"]);
        if (isSingleSelectionProduct(receiptLine)) {
            if (typeof singleLineProductQuantity[receiptLine.rootProductId] === 'number') {
                singleLineProductQuantity[receiptLine.rootProductId] += 1;
            }
            else {
                singleLineProductQuantity[receiptLine.rootProductId] = 1;
            }
        }
        if (canApplyDiscount(receiptLine)) {
            if (receiptLine.discount.discountType === 'simpleDiscountFixedAmount') {
                if (receiptLine.type === G6ReceiptLineType.Section) {
                    noMainProductDiscount[receiptLine.absoluteRootProductId] = receiptLine.discount.discountValue;
                    formattedReceiptLines.push(receiptLine);
                }
                else if (receiptLine.type === G6ReceiptLineType.Product) {
                    const newPrice = calculateSimpleDiscountFixedAmount(receiptLine);
                    const oldPrice = receiptLine.price;
                    formattedReceiptLines.push(Object.assign(Object.assign({}, trimmedLine), { price: newPrice, oldPrice: oldPrice, totalPrice: receiptLine.quantity * newPrice, oldTotalPrice: receiptLine.quantity * oldPrice }));
                }
            }
            else if (receiptLine.discount.discountType === 'percentDiscount') {
                if (receiptLine.type === G6ReceiptLineType.Product) {
                    if (isNumber(receiptLine.discount.discountValue)) {
                        const newPrice = (receiptLine.price * 100 - receiptLine.price * receiptLine.discount.discountValue) / 100;
                        const oldPrice = receiptLine.price;
                        formattedReceiptLines.push(Object.assign(Object.assign({}, trimmedLine), { price: newPrice, oldPrice: oldPrice, totalPrice: receiptLine.quantity * newPrice, oldTotalPrice: receiptLine.quantity * oldPrice }));
                    }
                    else {
                        formattedReceiptLines.push(Object.assign({}, receiptLine));
                    }
                }
                else {
                    formattedReceiptLines.push(Object.assign({}, receiptLine));
                }
            }
            else if (receiptLine.discount.discountType === 'bulkDiscount' && !isSingleSelectionProduct(receiptLine)) {
                const minQuantityToApply = parseFloat(receiptLine.discount.options.minQuantityToApply.toString());
                const newUnitPrice = parseFloat(receiptLine.discount.options.newUnitPrice.toString());
                if (isNumber(minQuantityToApply) && isNumber(newUnitPrice) && receiptLine.quantity >= minQuantityToApply) {
                    const newPrice = newUnitPrice;
                    const oldPrice = receiptLine.price;
                    formattedReceiptLines.push(Object.assign(Object.assign({}, trimmedLine), { price: newPrice, oldPrice: oldPrice, totalPrice: receiptLine.quantity * newPrice, oldTotalPrice: receiptLine.quantity * oldPrice }));
                }
                else {
                    formattedReceiptLines.push(Object.assign({}, receiptLine));
                }
            }
            else if (receiptLine.discount.discountType === 'twoForOne' && !isSingleSelectionProduct(receiptLine)) {
                const newPrice = (receiptLine.totalPrice * 100) / 2 / 100;
                const newPricePerUnit = (newPrice * 100) / receiptLine.quantity / 100;
                formattedReceiptLines.push(Object.assign(Object.assign({}, trimmedLine), { price: newPricePerUnit, totalPrice: newPrice }));
            }
            else if (receiptLine.discount.discountType === 'twoForOne' && isSingleSelectionProduct(receiptLine)) {
                if (forViking) {
                    const newPrice = (price * 100) / 2 / 100;
                    formattedReceiptLines.push(Object.assign(Object.assign({}, trimmedLine), { price: newPrice, totalPrice: newPrice }));
                }
                else {
                    if (singleLineProductQuantity[receiptLine.rootProductId] === 2) {
                        formattedReceiptLines.push(Object.assign(Object.assign({}, trimmedLine), { price: 0, totalPrice: 0 }));
                        singleLineProductQuantity[receiptLine.rootProductId] = 0;
                    }
                    else {
                        formattedReceiptLines.push(receiptLine);
                    }
                }
            }
        }
        else {
            if (receiptLine.type === G6ReceiptLineType.Product) {
                if (noMainProductDiscount[receiptLine.absoluteRootProductId] > 0) {
                    if (receiptLine.price >= noMainProductDiscount[receiptLine.absoluteRootProductId]) {
                        const newPrice = price - noMainProductDiscount[receiptLine.absoluteRootProductId];
                        formattedReceiptLines.push(Object.assign(Object.assign({}, trimmedLine), { price: newPrice, totalPrice: newPrice }));
                        noMainProductDiscount[receiptLine.absoluteRootProductId] = 0;
                    }
                    else {
                        noMainProductDiscount[receiptLine.absoluteRootProductId] -= price;
                        formattedReceiptLines.push(Object.assign(Object.assign({}, trimmedLine), { price: 0, totalPrice: 0 }));
                    }
                }
                else {
                    formattedReceiptLines.push(receiptLine);
                }
            }
            else {
                formattedReceiptLines.push(receiptLine);
            }
        }
    }
    return formattedReceiptLines;
};
export const mapToReceiptLinesG6 = (topLevelOrderLines, options = {}) => {
    const projected = projectReceiptLines(topLevelOrderLines || []);
    return applyPresentationDiscounts(projected, !!options.forViking);
};
export default mapToReceiptLinesG6;
